import React, { useCallback, useContext, createContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import QuickView from 'components/QuickView';
import { useFetch } from 'use-http';

const quickViewContext = createContext();

export function useQuickView()
{
   return useContext(quickViewContext);
}

export function QuickViewCore({ children })
{
   //inizializzo l'oggetto per la chiamata fetch
   const fetcher = useFetch(process.env.REACT_APP_BASE_API_URL, { cachePolicy: 'no-cache' });
   //inizializzo lo stato nel quale salvare l'elemento che fa da anchor alla quick view
   const [popperAnchor, setPopperAnchor] = useState(null);
   //inizializzo lo stato nel quale salvare il dato dell'operatore da visualizzare nella quick view
   const [operatoreData, setOperatoreData] = useState(null);
   const [loading, setLoading] = useState(false);
   const cacheOperatoriRef = useRef(new Map());
   const activeRequestKeyRef = useRef(null);

   const quickViewOpen = Boolean(popperAnchor);

   const getQueryString = useCallback((params = null) =>
   {
      const filteredParams = Object.entries(params || {})
         .filter(([, value]) => value !== undefined && value !== null && value !== '');

      if (filteredParams.length === 0)
      {
         return '';
      }

      filteredParams.sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey));

      return `?${new URLSearchParams(filteredParams).toString()}`;
   }, []);

   const getRequestKey = useCallback((idOperatore, params = null) =>
   {
      return `${idOperatore}${getQueryString(params)}`;
   }, [getQueryString]);

   const hideQuickView = useCallback(() =>
   {
      activeRequestKeyRef.current = null;
      setLoading(false);
      setPopperAnchor(null);
      setOperatoreData(null);
   }, []);

   const showQuickView = useCallback(async (el, idOperatore, params = null) =>
   {
      if (!el || !idOperatore)
      {
         return;
      }

      const requestKey = getRequestKey(idOperatore, params);
      const inCache = cacheOperatoriRef.current.get(requestKey);

      activeRequestKeyRef.current = requestKey;
      setPopperAnchor(el);

      if (inCache)
      {
         setLoading(false);
         setOperatoreData(inCache);
         return;
      }

      setLoading(true);
      setOperatoreData(null);

      try
      {
         const querystring = getQueryString(params);
         const data = await fetcher.request.get(`/api/anagrafiche/operatori/${idOperatore}/${querystring}`);

         if (activeRequestKeyRef.current !== requestKey)
         {
            return;
         }

         if (fetcher.error || !fetcher.response.ok || !data)
         {
            setOperatoreData(null);
            return;
         }

         cacheOperatoriRef.current.set(requestKey, data);
         setOperatoreData(data);
      }
      catch (error)
      {
         if (activeRequestKeyRef.current === requestKey)
         {
            setOperatoreData(null);
         }
      }
      finally
      {
         if (activeRequestKeyRef.current === requestKey)
         {
            setLoading(false);
         }
      }
   }, [fetcher, getQueryString, getRequestKey]);

   useEffect(() =>
   {
      return () =>
      {
         activeRequestKeyRef.current = null;
      };
   }, []);

   return (
      <quickViewContext.Provider value={
         {
            //metodi
            showQuickView,
            hideQuickView,
         }
      }>
         {children}
         <QuickView
            open={quickViewOpen}
            anchorEl={popperAnchor}
            placement="auto"
            onClose={() => hideQuickView()}
            sx={{
               zIndex: 1310,
               left: '-9999px',
               'position': 'fixed',
            }}
            modifiers={[
               {
                  name: "offset",
                  options: {
                     offset: [0, 10],
                  },
               }
            ]}
            loading={loading}
            data={operatoreData}
         />
      </quickViewContext.Provider>
   );
}

QuickViewCore.propTypes = {
   children: PropTypes.node,
};
